<template>
  <NuxtLayout :name="setLayout">
    <div class="pb-12">
      <template v-if="!isLoggedIn">
        <Loading v-if="pending" />
        <form
          v-else
          class="product-bluebox mt-2 md:mt-4 mb-12 bg-white-blue md:p-5"
          @submit.prevent="getBooking()"
        >
          <div class="flex flex-wrap -mx-4">
            <div class="md:w-6/12 px-4">
              <h5 data-i18n="mybookingLoginTitle">
                {{ $t('mybookingLoginTitle') }}
              </h5>
              <div
                class="mb-12 pr-0 md:pr-3"
                data-i18n="mybookingNote"
                v-html="$t('mybookingNote')"
              />
            </div>
            <div class="md:w-6/12 px-4">
              <div
                v-if="error"
                class="alert alert-danger"
              >
                {{ error?.message }}
              </div>
              <div class="form-group">
                <label
                  for="customerId"
                  data-i18n="formCustomerId"
                >{{ $t('formCustomerId') }}</label>
                <input
                  id="customerId"
                  v-model="formData.customerId"
                  class="form-control w-full"
                  type="customerId"
                  name="customerId"
                  autocomplete="customerId"
                  :class="{ 'is-invalid': v$.customerId.$error }"
                />
                <div
                  v-if="v$.customerId.$error"
                  class="invalid-feedback"
                  data-i18n="formFieldRequiredError"
                >
                  {{ $t('formFieldRequiredError') }}
                </div>
              </div>
              <div class="form-group">
                <label
                  for="reference"
                  data-i18n="formReferenceNr"
                >{{ $t('formReferenceNr') }}</label>
                <input
                  id="reference"
                  v-model="formData.reference"
                  class="form-control w-full"
                  type="reference"
                  name="reference"
                  autocomplete="reference"
                  :class="{ 'is-invalid': v$.reference.$error }"
                />
                <div
                  v-if="v$.reference.$error"
                  class="invalid-feedback"
                  data-i18n="formFieldRequiredError"
                >
                  {{ $t('formFieldRequiredError') }}
                </div>
              </div>
              <div class="form-group text-right mt-6">
                <button
                  class="btn btn-xl btn-blue"
                  type="submit"
                  data-i18n="formLoginMyBooking"
                  :disabled="pending"
                >
                  {{ $t('formLoginMyBooking') }}
                </button>
              </div>
            </div>
          </div>
        </form>
      </template>
      <template v-else>
        <PrettyTabsNav
          v-if="activities.length"
          class="pretty-tabs-layout-column pretty-tabs-layout-md-row"
          :tabs="navTabs"
          :locale-urls="localeURLs.mytripSlug"
          keep-query-params
          @on-tab-change="onTabChange"
        />
        <Loading v-if="pending" />
        <template v-else>
          <LazyMyBookingTab v-if="activeTab === 'mybooking'" />
          <LazyActivityCalendarTab v-if="activeTab === 'activitycalendar'" />
        </template>
      </template>
    </div>
  </NuxtLayout>
</template>

<script setup>
import { required } from '@vuelidate/validators'
import { useVuelidate } from '@vuelidate/core'
import { callWithNuxt } from '#app'

definePageMeta({
  transition: 'pageonly',
})

const route = useRoute()
const localeStore = useLocaleStore()
const myTripStore = useMyTripStore()
const rootStore = useRootStore()
const whitelabelStore = useWhitelabelStore()
const { urls: localeURLs } = useLocale()
const { t } = useI18n()
const nuxtApp = useNuxtApp()
const { $sentryCaptureException } = nuxtApp

const {
  activities,
} = storeToRefs(myTripStore)

useHead({
  title: () => `${t('mybookingTitle')}`,
})

const rules = computed(() => ({
  customerId: { required },
  reference: { required },
}))

const formData = reactive({
  customerId: null,
  reference: null,
})

const v$ = useVuelidate(rules, formData)

const myTripSlugLocaleURLs = localeStore.urls.mytripSlug

const activeTab = ref(findActiveSlugKey(myTripSlugLocaleURLs, route.params, 'tab'))

const { whitelabel } = storeToRefs(whitelabelStore)

const setLayout = computed(() => {
  if (whitelabel.value) {
    return 'charter-whitelabel'
  }

  return isSol()
    ? 'charter-page'
    : 'banner-default'
})

const isLoggedIn = computed(() => {
  return (
    parseInt(route.query.k) === myTripStore.booking?.customer?.value &&
    parseInt(route.query.b) === myTripStore.booking?.ocr?.value
  )
})

const onTabChange = (newActiveTab) => {
  activeTab.value = newActiveTab
}

const { $isSol } = useNuxtApp()

const { data: { value: { navTabs } } } = await useAsyncData(async () => {
  const [heroImage] = await Promise.all([
    rootStore.fetchHeroImage({ page: 'mybooking' }),
    localeStore.SET_PAGE_WIDE_TRANSLATIONS(createPageWideTranslation(['mybookingTitle', 'mybookingSubtitle'])),
  ])

  if ($isSol) {
    rootStore.SET_PAGE_BANNERTYPE('CAMPAIGN')
  }

  rootStore.SET_PAGE_BANNER({
    title: t('mybookingTitle'),
    type: 'small',
    subtitle: t('mybookingSubtitle'),
    background: {
      image: {
        url: heroImage?.picture_cloudinary,
      },
    },
  })

  // Build navbar
  const myTripLocaleURL = localeStore.urls.mytrip

  const navTabs = [
    {
      text: 'myTripPage.myBookingTab',
      to: myTripLocaleURL,
      slug: myTripSlugLocaleURLs.mybooking,
      localeUrlKey: 'mybooking',
    },
    {
      text: 'myTripPage.activityCalendarTab',
      to: `${myTripLocaleURL}${myTripSlugLocaleURLs.activitycalendar}`,
      slug: myTripSlugLocaleURLs.activitycalendar,
      localeUrlKey: 'activitycalendar',
    },
  ]

  return {
    navTabs,
  }
})

const { refresh: getBooking, pending, error } = await useAsyncData(async () => {
  if (route.query.k) {
    formData.customerId = parseInt(route.query.k)
  }
  if (route.query.b) {
    formData.reference = parseInt(route.query.b)
  }

  const { reference, customerId } = formData
  if (reference === null || customerId === null) {
    myTripStore.SET_DATA(null)
    return
  }

  if (process.browser) {
    v$.value.$touch()
  }

  if (v$.value.$invalid || isLoggedIn.value) {
    return
  }

  try {
    await myTripStore.getReservation({ customerId, reference })
    await myTripStore.getActivities({ customerId, reference })

    const queryParams = `?k=${customerId}&b=${reference}`
    // When navigating back to any mybooking tab: don't change route
    if (!route.fullPath.includes(queryParams)) {
      return callWithNuxt(nuxtApp, navigateTo, [`${localeURLs.mytrip}${queryParams}`]).catch(() => {})
    }
  } catch (e) {
    if (e.data?.error) {
      throw new Error(e.data.message)
    }

    $sentryCaptureException(e)
    throw new Error(t('serverError'))
  }
})
</script>
